import { debounce, get } from 'lodash';
import { useSelector } from 'react-redux';
import { userSelector } from 'app/selectors';
import StyledForm from './StyledForm';
import { useForm } from 'react-hook-form';
import CustomInput from 'components/fields/input2';
import Button from 'components/Button';
import MySelect from 'components/MySelect';
import { useEffect, useState } from 'react';
import { handleError, request } from 'app/api';
import CustomCheckbox from 'components/fields/CustomCheckbox';
import { CircularProgress, IconButton, Skeleton } from '@mui/material';
import { useContext } from 'react';
import TableRow from 'components/table/CustomTableRow';
import TableCol from 'components/table/CustomTableCol';
import { LanguageContext } from 'components/language';
import { toast } from 'react-toastify';
import { ReactComponent as TrashIcon } from 'assets/img/Trash_light.svg';
import { ReactComponent as EditIcon } from 'assets/img/Edit_light.svg';
import { useDialog, useMessage } from 'app/hooks';
import StyledModal from './StyledUpdateModal';

const initialValues = {
    hotelId: null,
    bookingComPropertyId: '',
    bookingComPropertyName: '',
    isActive: false,
};

const HotelsIntegration = () => {
    const user = useSelector(userSelector);
    const hotels = get(user, 'hotel', []);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const { t } = useContext(LanguageContext);
    const { setMessage } = useMessage();
    const { handleSubmit, control, reset, setValue } = useForm({ defaultValues: initialValues });
    const [isOpen, handleShow, hide] = useDialog();
    const [activeBookingComProperty, setActiveBookingComProperty] = useState(null);

    const getBookingHotel = (id) => {
        request({
            url: `/booking-com/property?bookingComPropertyId=${id}`,
            success: ({ data }) => {
                setValue('bookingComPropertyName', get(data, 'data.property_name', ''));
            },
        });
    };

    const fetchData = () => {
        setLoading(true);
        request({
            url: '/booking-com/properties',
            success: ({ data }) => {
                setData(data);
                setLoading(false);
            },
            fail: () => {
                setLoading(false);
            },
        });
    };

    const handleDelete = (hotelId) => {
        setMessage({
            text: 'Are you sure you want to permanently reconnect the hotel?',
            confirm: true,

            yes: () => {
                request({
                    method: 'post',
                    url: `booking-com/property/disconnect?hotelId=${hotelId}`,
                    success: () => {
                        setMessage({});
                        fetchData();
                    },
                    fail: (e) => handleError(e),
                });
            },
            no: () => {
                setMessage({});
            },
        });
    };

    useEffect(() => {
        fetchData();
    }, []);

    const onSubmit = (data) => {
        setUploading(true);
        request({
            url: `/booking-com/property/connect`,
            method: 'post',
            params: data,
            success: () => {
                reset(initialValues);
                toast.success(t('HOTEL_CONNECTED_SUCCESSFULLY'));
                fetchData();
            },
            fail: handleError,
            finish: () => {
                setUploading(false);
            },
        });
    };

    return (
        <div className="main-table-container">
            <StyledForm onSubmit={handleSubmit(onSubmit)}>
                <MySelect
                    control={control}
                    name="hotelId"
                    options={hotels}
                    isSearchable
                    isClearable
                    sx={{ width: '200px' }}
                    placeholder={t('HOTEL')}
                />
                <CustomInput
                    onChange={debounce(getBookingHotel, 300)}
                    type="number"
                    control={control}
                    name="bookingComPropertyId"
                    placeholder={t('BOOKING_COM_HOTEL_ID')}
                />
                <CustomInput control={control} name="bookingComPropertyName" placeholder={t('BOOKING_COM_HOTEL_NAME')} />
                <CustomCheckbox control={control} name="active" label={t('ACTIVE')} />
                <Button type="submit" disabled={uploading}>
                    {uploading ? <CircularProgress size={20} /> : t('SAVE')}
                </Button>
            </StyledForm>

            <div className="category-body">
                <TableRow head>
                    <TableCol width={30}>#</TableCol>
                    <TableCol>{t('NAME')}</TableCol>
                    <TableCol>{t('BOOKING_COM_HOTEL_ID')}</TableCol>
                    <TableCol>{t('BOOKING_COM_HOTEL_NAME')}</TableCol>
                    <TableCol width={150}>{t('STATUS')}</TableCol>
                    <TableCol width={100}>{t('ACTIONS')}</TableCol>
                </TableRow>

                <div className="body">
                    {loading
                        ? Array(4)
                              .fill(0)
                              .map((_, i) => (
                                  <TableRow key={i}>
                                      <TableCol>
                                          <Skeleton width={'100%'} animation="wave" height={35} />
                                      </TableCol>
                                  </TableRow>
                              ))
                        : data?.map((item, index) => (
                              <TableRow key={item.id}>
                                  <TableCol width={30}>{index + 1}</TableCol>
                                  <TableCol>{item.name}</TableCol>
                                  <TableCol>{item.bookingComPropertyId}</TableCol>
                                  <TableCol>{item.bookingComPropertyName}</TableCol>
                                  <TableCol width={150}>{item.bookingComActive ? t('ACTIVE') : t('INACTIVE')}</TableCol>
                                  <TableCol width={100}>
                                      <IconButton
                                          onClick={() => {
                                              handleShow();
                                              setActiveBookingComProperty(item);
                                          }}
                                      >
                                          <EditIcon />
                                      </IconButton>
                                      <IconButton onClick={() => handleDelete(item.id)}>
                                          <TrashIcon />
                                      </IconButton>
                                  </TableCol>
                              </TableRow>
                          ))}
                </div>
            </div>
            <SelectHotelModal isOpen={isOpen} onClose={hide} onSuccess={fetchData} hotels={hotels} bookingComProperty={activeBookingComProperty} />
        </div>
    );
};
export default HotelsIntegration;

const SelectHotelModal = ({ isOpen, onClose, onSuccess, hotels, bookingComProperty }) => {
    const { t } = useContext(LanguageContext);
    const [updating, setUpdating] = useState(false);

    const { handleSubmit, control, reset } = useForm({ defaultValues: { hotelId: null } });

    const onSubmit = (data) => {
        setUpdating(true);
        request({
            url: `/booking-com/property/edit`,
            method: 'post',
            params: {
                ...data,
                bookingComPropertyId: bookingComProperty.bookingComPropertyId,
                bookingComPropertyName: bookingComProperty.bookingComPropertyName,
                active: bookingComProperty.bookingComActive,
            },
            success: () => {
                toast.success(t('HOTEL_UPDATED_SUCCESSFULLY'));
                reset({ hotelId: null });
                onClose();
                onSuccess();
            },
            fail: handleError,
            finish: () => {
                setUpdating(false);
            },
        });
    };

    return (
        <StyledModal
            active={isOpen}
            onClose={() => {
                reset({ hotelId: null });
                onClose();
            }}
        >
            <div className="modal-content">
                <h4>{t('SELECT_A_HOTEL')}</h4>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="modal-content-body">
                        <MySelect
                            nullable={false}
                            control={control}
                            name="hotelId"
                            options={hotels}
                            sx={{ width: '100%' }}
                            placeholder={t('HOTEL')}
                        />
                    </div>
                    <div className="modal-content-footer">
                        <Button loading={updating} color="primary" type="submit">
                            {t('SAVE')}
                        </Button>
                    </div>
                </form>
            </div>
        </StyledModal>
    );
};
